import {Box, Button, FormControlLabel, IconButton, List, ListItem, ListItemIcon, ListItemText, Switch, Tab, Tabs,
    TextField, Typography} from "@mui/material";
import React, {ChangeEvent, useContext, useState} from "react";
import {AcceptedInvite, Invites as InvitesType, PendingInvite, Projects, Roles, Users as UsersType}
    from "../../redux/types/firebase";
import {TabPanel} from "./TabPanel";
import {selectProjects} from "../../redux/selectors/firebase";
import {connect} from "react-redux";
import {DescribeRoles} from "./Roles";
import {UserRoles} from "./UserRoles";
import {FirebaseContext} from "../firebase/FirebaseProvider";
import {Delete} from "@mui/icons-material";
import { getAuth, sendSignInLinkToEmail } from "firebase/auth";
import makeStyles from "@mui/styles/makeStyles";

const useStyles = makeStyles(theme => ({
    invites: {
        maxHeight: 500,
        overflowY: "auto",
        '&::-webkit-scrollbar': {
            width: '0.4em'
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,.1)',
            borderRadius: '0.4em'
        }
    },
    list: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: "min-content"
    }
}));

interface InviteLineProps {
    invite: string
    primary: string
    secondary: string
    onDelete: (event: React.MouseEvent<HTMLElement>) => void
}
function InviteLine({invite, primary, secondary, onDelete}: InviteLineProps) {
    return (
        <ListItem key={invite}>
            <ListItemText
                key={invite} primary={primary} secondary={secondary}/>
            <ListItemIcon>
                <IconButton data-invite={invite} onClick={onDelete} size="large">
                    <Delete/>
                </IconButton>
            </ListItemIcon>
        </ListItem>
    );
}

interface Props {
    invites: InvitesType
    projects: Projects
    users: UsersType
}

function Invites({invites, projects, users}: Props) {
    const classes = useStyles();
    const [accessLink, setAccessLink] = useState(false);
    const [inviteEmail, setInviteEmail] = useState("");
    const [inviteRoles, setInviteRoles] = useState({});
    const [inviteTab, setInviteTab] = useState(0);
    const {push, removeInvite} = useContext(FirebaseContext);
    const handleChangeInviteTab = (event: ChangeEvent<{}>, value: any) => {
        setInviteTab(value);
    };
    const handleRolesChange = (roles: Roles) => {
        setInviteRoles(roles);
    }
    const handleEmailChange = ({currentTarget}: React.ChangeEvent<HTMLInputElement>) => {
        setInviteEmail(currentTarget.value);
    }
    const handleInviteClick = () => {
        if (accessLink) {
            push("invites", {email: inviteEmail, roles: inviteRoles}).then(() => {
                sendSignInLinkToEmail(getAuth(), inviteEmail, {
                    handleCodeInApp: true,
                    url: `${window.location.protocol}//${window.location.host}`
                }).then(() =>{
                    setInviteEmail("");
                    setInviteRoles({});
                }).catch(console.error);
            });
        } else {
            push("invites", {email: inviteEmail, roles: inviteRoles}).then(() => {
                setInviteEmail("");
                setInviteRoles({});
            });
        }
    }
    const handleInviteDelete = ({currentTarget}: React.MouseEvent<HTMLElement>) => {
        currentTarget.dataset.invite !== undefined && removeInvite(currentTarget.dataset.invite);
    }
    const pending = Object.keys(invites || {}).map(key => {
        const pending = invites[key] as PendingInvite;

        if (!pending.email) return null;
        return <InviteLine key={key} invite={key} primary={pending.email} secondary={DescribeRoles(pending.roles, projects)}
                           onDelete={handleInviteDelete}/>;
    }).filter(element => element);
    const accepted = Object.keys(invites || {}).map(key => {
        const accepted = invites[key] as AcceptedInvite;

        if (!accepted.invitedUser) return null;

        const user = users[accepted.invitedUser];
        const primary = user?.name || accepted.invitedUser;
        const secondary = accepted.accepted ? (new Date(accepted.accepted)).toLocaleDateString() : "";

        return <InviteLine key={key} invite={key} primary={primary} secondary={secondary} onDelete={handleInviteDelete}/>;
    }).filter(element => element);
    const roles = DescribeRoles(inviteRoles, projects);

    return (
        <Box>
            <Tabs value={inviteTab} onChange={handleChangeInviteTab}>
                <Tab label="Invite"/>
                <Tab label="Pending"/>
                <Tab label="Accepted"/>
            </Tabs>
            <TabPanel value={inviteTab} index={0}>
                <Box display="flex" m={1}>
                    <Box flexGrow={1}>
                        <TextField fullWidth label="Email" value={inviteEmail} onChange={handleEmailChange} size="small"/>
                    </Box>
                    <Box marginLeft={1}>
                        <FormControlLabel control={
                            <Switch checked={accessLink} onChange={() => setAccessLink(!accessLink)}/>
                        } label="Access link (requires no password)"/>
                    </Box>
                </Box>
                <Box margin={2}>
                    <UserRoles onRolesChange={handleRolesChange} projects={projects} roles={inviteRoles}/>
                </Box>
                <Box display="flex">
                    <Box flexGrow={1}>
                        <Typography>{roles && inviteEmail ? `Roles: ${roles}` :
                            "Set some roles and email address to enable invite"}</Typography>
                    </Box>
                    <Button disabled={inviteEmail === "" || roles === ""} variant="contained" onClick={handleInviteClick}>
                        Invite
                    </Button>
                </Box>
            </TabPanel>
            <TabPanel value={inviteTab} index={1}>
                <Box className={classes.invites}>
                    <List className={classes.list}>
                        {pending}
                    </List>
                </Box>
            </TabPanel>
            <TabPanel value={inviteTab} index={2}>
                <Box className={classes.invites}>
                    <List className={classes.list}>
                        {accepted}
                    </List>
                </Box>
            </TabPanel>
        </Box>
    );
}

const mapStateToProps = (store: never) => ({
    projects: selectProjects(store)
});

export default connect(mapStateToProps, {
})(Invites);
